if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      cartUpdateUnsubscriber = undefined;
      explicitChoiceInvalidHandlerAttached = false;
      explicitChoiceRequestSequence = 0;
      explicitChoiceRequest = null;
      explicitChoicePending = false;
      explicitChoiceReconnectReset = false;

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      get requiresExplicitChoice() {
        return this.variantSelectors?.dataset.requiresExplicitChoice === 'true';
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.currentVariant = this.explicitChoiceReconnectReset ? null : this.getSelectedVariant(this);
        this.initExplicitChoiceGuard();
        if (this.explicitChoiceReconnectReset && this.requiresExplicitChoice) {
          this.resetExplicitChoiceControls();
          this.explicitChoiceReconnectReset = false;
        }
        this.applyExplicitChoiceState(this.currentVariant, {
          announce: false,
          variantDisabled: this.explicitChoiceSubmit?.hasAttribute('disabled') ?? null,
        });
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.abortController?.abort();
        this.abortController = undefined;
        this.pendingRequestUrl = null;
        this.explicitChoiceRequestSequence += 1;
        this.explicitChoiceRequest = null;
        if (this.explicitChoicePending) this.explicitChoiceReconnectReset = true;
        this.explicitChoicePending = false;
        cancelAnimationFrame(this.explicitChoiceInvalidFrame);
        cancelAnimationFrame(this.explicitChoiceStatusFrame);
        cancelAnimationFrame(this.explicitChoiceErrorFrame);
        clearInterval(this.timer);
        if (this.explicitChoiceInvalidHandlerAttached) {
          this.removeEventListener('invalid', this.handleExplicitChoiceInvalid, true);
          this.explicitChoiceInvalidHandlerAttached = false;
        }
      }

      initExplicitChoiceGuard() {
        if (!this.requiresExplicitChoice) return;

        const picker = this.variantSelectors;
        this.explicitChoiceMessage = picker.dataset.choiceMessage || '';
        this.explicitChoiceButtonLabel = picker.dataset.choiceButtonLabel || '';
        this.explicitChoiceHiddenId = this.productForm?.querySelector('input[name="id"]');
        this.explicitChoiceSubmit = this.productForm?.querySelector('[data-explicit-choice-submit]');
        this.explicitChoicePayment = this.productForm?.querySelector('[data-explicit-choice-payment]');
        this.explicitChoiceStatus = this.querySelector(`#${picker.dataset.choiceStatusId}`);
        this.explicitChoiceError = this.querySelector(`#${picker.dataset.choiceErrorId}`);

        if (!this.explicitChoiceInvalidHandlerAttached) {
          this.addEventListener('invalid', this.handleExplicitChoiceInvalid, true);
          this.explicitChoiceInvalidHandlerAttached = true;
        }
      }

      handleExplicitChoiceInvalid = (event) => {
        if (!this.requiresExplicitChoice || !event.target.matches('[data-explicit-choice-input]')) return;

        event.preventDefault();
        this.querySelectorAll('[data-explicit-choice-input]').forEach((input) => {
          input.setAttribute('aria-invalid', 'true');
        });

        if (this.explicitChoiceStatus) this.explicitChoiceStatus.textContent = '';
        if (this.explicitChoiceError) this.explicitChoiceError.textContent = '';

        cancelAnimationFrame(this.explicitChoiceInvalidFrame);
        this.explicitChoiceInvalidFrame = requestAnimationFrame(() => {
          if (this.explicitChoiceError) this.explicitChoiceError.textContent = this.explicitChoiceMessage;
          this.querySelector('[data-explicit-choice-input]:required:not(:disabled)')?.focus();
        });
      };

      clearExplicitChoiceError() {
        if (!this.requiresExplicitChoice) return;
        cancelAnimationFrame(this.explicitChoiceInvalidFrame);
        cancelAnimationFrame(this.explicitChoiceErrorFrame);
        this.querySelectorAll('[data-explicit-choice-input]').forEach((input) => {
          input.setAttribute('aria-invalid', 'false');
        });
        if (this.explicitChoiceError) this.explicitChoiceError.textContent = '';
      }

      normalizeExplicitChoiceValues(values = []) {
        return values.filter(Boolean).map(String).sort();
      }

      getExplicitChoiceValues(root) {
        const picker = root?.matches?.('variant-selects') ? root : root?.querySelector?.('variant-selects');
        if (!picker) return [];
        return this.normalizeExplicitChoiceValues(
          Array.from(
            picker.querySelectorAll(
              'select option[selected][data-option-value-id], fieldset input:checked[data-option-value-id]'
            )
          ).map((input) => input.dataset.optionValueId)
        );
      }

      getResponseProductInfo(html, viewMode) {
        const quickView = html.querySelector('#MainProduct-quick-view__content');
        const root = quickView && viewMode === 'quick-view' ? quickView.content : html;
        return root.querySelector('product-info');
      }

      responseMatchesExplicitChoice(html, request) {
        if (!this.requiresExplicitChoice) return true;
        const responseProductInfo = this.getResponseProductInfo(html, request.viewMode);
        const returnedVariant = responseProductInfo ? this.getSelectedVariant(responseProductInfo) : null;
        const returnedValues = this.getExplicitChoiceValues(responseProductInfo);
        return (
          Boolean(returnedVariant?.id) &&
          returnedValues.length === request.optionValues.length &&
          returnedValues.every((value, index) => value === request.optionValues[index])
        );
      }

      isCurrentExplicitChoiceRequest(request) {
        return !this.requiresExplicitChoice || this.explicitChoiceRequest?.sequence === request.sequence;
      }

      notifyStickyExplicitChoice(method, ...args) {
        if (!this.requiresExplicitChoice || this.dataset.viewMode === 'quick-view') return;
        const sticky = document.querySelector(`sticky-atc-bar[data-product-id="${CSS.escape(this.productId)}"]`);
        sticky?.[method]?.(...args);
      }

      enterExplicitChoicePending(request) {
        if (!this.requiresExplicitChoice) return;
        this.explicitChoicePending = true;
        this.currentVariant = null;
        this.clearExplicitChoiceError();
        if (this.explicitChoiceStatus) this.explicitChoiceStatus.textContent = '';
        this.applyExplicitChoiceState(null, { announce: false, pending: true });
        this.notifyStickyExplicitChoice('handleMainExplicitChoicePending', request);
      }

      resetExplicitChoiceControls() {
        if (!this.requiresExplicitChoice) return;
        this.querySelectorAll('[data-explicit-choice-input]').forEach((input) => {
          input.setAttribute('aria-invalid', 'false');
          if (input.matches('input[type="radio"]')) input.checked = false;
          if (input.matches('select')) {
            input.value = '';
            input.querySelectorAll('option').forEach((option) => {
              option.toggleAttribute('selected', option.value === '');
            });
          }
        });
        this.querySelectorAll('[data-selected-swatch-value]').forEach((label) => {
          label.textContent = '';
        });
      }

      failExplicitChoiceRequest(request, error) {
        if (!this.requiresExplicitChoice || !this.isCurrentExplicitChoiceRequest(request)) return;
        this.explicitChoicePending = false;
        this.explicitChoiceRequest = null;
        this.explicitChoiceReconnectReset = true;
        this.currentVariant = null;
        this.resetExplicitChoiceControls();
        this.applyExplicitChoiceState(null, { announce: false, locked: true });
        this.querySelectorAll('[data-explicit-choice-input]').forEach((input) => {
          input.setAttribute('aria-invalid', 'true');
        });
        if (this.explicitChoiceStatus) this.explicitChoiceStatus.textContent = '';
        const unavailableMessage = this.decoded(FoxTheme.variantStrings.unavailable || '');
        const recoverableMessage = [unavailableMessage, this.explicitChoiceMessage].filter(Boolean).join(' ');
        if (this.explicitChoiceError) this.explicitChoiceError.textContent = '';
        cancelAnimationFrame(this.explicitChoiceErrorFrame);
        this.explicitChoiceErrorFrame = requestAnimationFrame(() => {
          if (this.explicitChoiceError) this.explicitChoiceError.textContent = recoverableMessage;
        });
        this.notifyStickyExplicitChoice('handleMainExplicitChoiceFailure', request, recoverableMessage);
        if (error) console.error(error);
      }

      applyExplicitChoiceState(variant, options = {}) {
        if (!this.requiresExplicitChoice) return;

        const { announce = true, variantDisabled = null, pending = false, locked = false } = options;
        const hasChoice = Boolean(variant?.id);
        const isUnavailable = hasChoice && (variantDisabled ?? variant.available === false);
        const blocksSubmission = pending || locked;
        const picker = this.variantSelectors;

        picker.dataset.explicitChoice = pending ? 'pending' : locked ? 'error' : hasChoice ? 'true' : 'false';

        if (this.explicitChoiceHiddenId) {
          this.explicitChoiceHiddenId.value = hasChoice ? variant.id : '';
          this.explicitChoiceHiddenId.disabled = !hasChoice;
        }

        if (this.explicitChoiceSubmit) {
          const submitText = this.explicitChoiceSubmit.querySelector('span');
          this.explicitChoiceSubmit.toggleAttribute('disabled', blocksSubmission || isUnavailable);
          this.explicitChoiceSubmit.toggleAttribute('aria-disabled', blocksSubmission || !hasChoice || isUnavailable);
          this.explicitChoiceSubmit.style.pointerEvents = blocksSubmission || isUnavailable ? 'none' : '';
          this.explicitChoiceSubmit.style.opacity = !hasChoice || isUnavailable ? '0.6' : '';

          if (submitText) {
            if (!hasChoice) {
              submitText.textContent = this.decoded(this.explicitChoiceButtonLabel);
            } else if (isUnavailable) {
              submitText.textContent = this.decoded(FoxTheme.variantStrings.soldOut);
            } else {
              submitText.textContent = this.decoded(FoxTheme.variantStrings.addToCart);
            }
          }
        }

        if (this.explicitChoicePayment) {
          const hidePayment = !hasChoice || isUnavailable;
          this.explicitChoicePayment.toggleAttribute('hidden', hidePayment);
          this.explicitChoicePayment.toggleAttribute('inert', hidePayment);
          if (hidePayment) {
            this.explicitChoicePayment.setAttribute('aria-hidden', 'true');
          } else {
            this.explicitChoicePayment.removeAttribute('aria-hidden');
          }
        }

        if (!hasChoice) {
          cancelAnimationFrame(this.explicitChoiceStatusFrame);
          if (this.explicitChoiceStatus) this.explicitChoiceStatus.textContent = '';
          return;
        }

        this.clearExplicitChoiceError();
        if (announce && this.explicitChoiceStatus) {
          this.explicitChoiceStatus.textContent = '';
          cancelAnimationFrame(this.explicitChoiceStatusFrame);
          this.explicitChoiceStatusFrame = requestAnimationFrame(() => {
            this.explicitChoiceStatus.textContent = variant.public_title || variant.title || '';
          });
        }
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        if (!this.requiresExplicitChoice) this.disableButtons();
        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          optionValues: selectedOptionValues,
          viewMode,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );

            if (!variant) {
              if (this.requiresExplicitChoice) {
                this.applyExplicitChoiceState(null, { announce: false });
              } else {
                this.setUnavailable();
              }
              return;
            }
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.initExplicitChoiceGuard();
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.currentVariant = null;
            if (this.requiresExplicitChoice) {
              this.applyExplicitChoiceState(null, { announce: false });
              FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
                data: {
                  sectionId: this.sectionId,
                  html,
                  variant: null,
                },
              });
            } else {
              this.setUnavailable();
            }
            return;
          }

          this.currentVariant = variant;

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku');
          updateSourceFromDestination('Inventory');
          updateSourceFromDestination('Badges');
          updateSourceFromDestination('PricePerItem');
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          updateSourceFromDestination('QuantityRules');
          updateSourceFromDestination('VolumeNote');

          const sizeChartRoot = this.requiresExplicitChoice ? this : document;
          HTMLUpdateUtility.viewTransition(
            sizeChartRoot.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const stickyAtcBar = document.getElementById(`shopify-section-sticky-atc-bar`);
          if (stickyAtcBar && (!this.requiresExplicitChoice || viewMode !== 'quick-view')) {
            stickyAtcBar.classList.remove('hidden');
          }

          const newAddButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const isDisabled = !newAddButton || newAddButton.hasAttribute('disabled');
          this.updateButtonsState(isDisabled, {
            updateText: true,
            text: isDisabled ? FoxTheme.variantStrings.soldOut : null,
          });
          this.applyExplicitChoiceState(variant, { variantDisabled: isDisabled });

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: variant,
              },
            })
          );
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, optionValues = [], viewMode = 'main-product', callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const request = {
          sequence: ++this.explicitChoiceRequestSequence,
          optionValues: this.normalizeExplicitChoiceValues(optionValues),
          requestUrl,
          targetId,
          viewMode,
        };
        if (this.requiresExplicitChoice) {
          this.explicitChoiceRequest = request;
          this.enterExplicitChoicePending(request);
        }

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Product section request failed with HTTP ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            if (!this.isCurrentExplicitChoiceRequest(request)) return null;
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            if (!this.responseMatchesExplicitChoice(html, request)) {
              throw new Error('Product section response did not resolve the exact requested variant.');
            }
            this.explicitChoicePending = false;
            this.explicitChoiceReconnectReset = false;
            callback(html);
            this.explicitChoiceRequest = null;
            return html;
          })
          .then((html) => {
            if (!html || !this.isConnected) return;
            // set focus to last clicked option value
            const focusRoot = this.requiresExplicitChoice ? this : document;
            focusRoot.querySelector(`#${CSS.escape(targetId)}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              this.failExplicitChoiceRequest(request, error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        const queryRoot = this.requiresExplicitChoice ? this : document;
        queryRoot
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            if (!input) return;
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = this.requiresExplicitChoice
          ? this.querySelector(`#ProductShare-${this.dataset.section}`)
          : document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      updateButtonsState(disabled, options = {}) {
        const { updateText = false, text = null, updateStyles = true } = options;

        // Handle main add button in product form
        const productForm = this.requiresExplicitChoice
          ? this.productForm
          : document.getElementById(`product-form-${this.dataset.section}`);
        if (productForm) {
          const addButton = productForm.querySelector('[name="add"]');
          const addButtonText = productForm.querySelector('[name="add"] > span');

          if (addButton) {
            if (disabled) {
              addButton.setAttribute('disabled', 'disabled');
              if (updateText && text && addButtonText) {
                addButtonText.textContent = this.decoded(text);
              }
            } else {
              const variantId = productForm.querySelector('[name="id"]')?.value;
              if (variantId) {
                addButton.removeAttribute('disabled');
                if (updateText && addButtonText) {
                  addButtonText.textContent = this.decoded(FoxTheme.variantStrings.addToCart);
                }
              }
            }

            if (updateStyles) {
              addButton.style.pointerEvents = disabled ? 'none' : '';
              addButton.style.opacity = disabled ? '0.6' : '';
            }
          }
        }

        if (this.requiresExplicitChoice) return;

        // Handle sticky ATC button
        const otherButtonSelectors = ['.sticky-atc-bar [name="add"]'];
        otherButtonSelectors.forEach((selector) => {
          const buttons = document.querySelectorAll(selector);
          buttons.forEach((button) => {
            if (disabled) {
              button.setAttribute('disabled', 'disabled');
              if (updateStyles) {
                button.style.pointerEvents = 'none';
                button.style.opacity = '0.6';
              }
            } else {
              button.removeAttribute('disabled');
              if (updateStyles) {
                button.style.pointerEvents = '';
                button.style.opacity = '';
              }
            }
          });
        });
      }

      decoded(text) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = text;
        const decoded = tempDiv.textContent;
        return decoded;
      }

      setUnavailable() {
        this.updateButtonsState(true, {
          updateText: true,
          text: FoxTheme.variantStrings.unavailable,
        });
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);
        const volumePricing = document.getElementById(`Volume-${this.dataset.section}`);
        const stickyAtcBar = document.getElementById(`shopify-section-sticky-atc-bar`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
        if (volumePricing) volumePricing.classList.add('hidden');
        if (stickyAtcBar) stickyAtcBar.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }

      disableButtons() {
        this.updateButtonsState(true);
      }

      enableButtons() {
        this.updateButtonsState(false);
      }
    }
  );
}
